import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Typography } from '@material-ui/core';
import { Grid } from '@mui/material';
import classNames from 'classnames';
import { SGSFormLayout } from 'components';
import { useCustomStyles } from 'core/mui/customStyles';
import { endOfMonth, format, formatISO, startOfMonth, subMonths } from 'date-fns';
import { useArretTravailFormContext } from 'features/arretTravail/contexts/ArretTravailFormProvider';
import useValidationEffects from 'features/arretTravail/hooks/useValidationEffects';
import { useEffect } from 'react';
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { SalaryNotFulTime } from '../SalaryInterruptionPeriodS3201';
import { useSalaryBaseSchema } from './schema';
import { TableSalaryBase } from './table';

type Props = {
    store: any;
    onSubmit: SubmitHandler<SalaryBaseS3201Schema>;
    actions?: JSX.Element;
};

export type SalaryBaseS3201 = {
    id: string;
    startDate: Date | null;
    endDate: Date | null;
    amount: string | null;
    interruptionActivityS3201: SalaryNotFulTime[];
};

export type SalaryBaseS3201Schema = {
    salaryBaseS3201: SalaryBaseS3201[];
};

const FormSalaryBaseS3201: React.FC<Props> = ({ onSubmit, store, actions }) => {
    const { activeStep, setStepFailed } = useArretTravailFormContext();
    const customStyles = useCustomStyles();

    const validationSchema: Yup.SchemaOf<SalaryBaseS3201Schema> = useSalaryBaseSchema();

    const lastDayWorked = new Date(store?.workStoppage?.infos?.lastDayWorked ?? null);
    const accidentDate = store?.workStoppage?.infos?.start ?? null;
    const nbrMonth = 3;
    const initialSalaires = Array.from({ length: nbrMonth }, (_, index) => {
        if (!accidentDate) return;
        const date = subMonths(new Date(accidentDate), index);

        return {
            id: `${index + 1}`,
            startDate: formatISO(startOfMonth(subMonths(date, 1)), { representation: 'complete' }),
            endDate: formatISO(endOfMonth(subMonths(date, 1)), { representation: 'complete' }),
            amount: '',
            interruptionActivityS3201: [],
        };
    });

    const startDate = format(startOfMonth(subMonths(new Date(accidentDate), 3)), 'dd/MM/yyyy');
    const endDate = format(endOfMonth(subMonths(new Date(accidentDate), 1)), 'dd/MM/yyyy');

    const methods = useForm<SalaryBaseS3201Schema>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            salaryBaseS3201: store?.salaryBaseS3201?.length ? store.salaryBaseS3201 : initialSalaires.reverse(),
        },
    });

    const {
        handleSubmit,
        control,
        formState: { errors },
    } = methods;

    useValidationEffects({ errors, activeStep, setStepFailed, control });

    const { fields: salaries, append } = useFieldArray<SalaryBaseS3201Schema, 'salaryBaseS3201', 'id'>({
        control,
        name: 'salaryBaseS3201',
        keyName: 'id',
    });

    const handleNewSalaire = () => {
        append({
            id: `${salaries.length + 1}`,
            startDate: null,
            endDate: null,
            amount: null,
            interruptionActivityS3201: [],
        });
    };

    useEffect(() => {
        if (salaries.length === 0) handleNewSalaire();
    }, [salaries]);

    console.log(errors);

    return (
        <FormProvider {...methods}>
            <SGSFormLayout onSubmit={handleSubmit(onSubmit)} actions={actions}>
                <Grid container justifyContent="center" style={{ marginBottom: '20px' }}>
                    <Grid item xs={12} md={3}></Grid>
                    <Grid item xs={12} md={6}>
                        <Box className={'text-center'}>
                            <Typography component={'span'} className={customStyles.txtBlueLight}>
                                Date du dernier jour de travail :{' '}
                            </Typography>
                            <Typography component={'span'} className={customStyles.txtBlueDarkBold}>
                                {format(lastDayWorked, 'dd/MM/yyyy')}
                            </Typography>
                        </Box>
                        <Box className={'text-center'}>
                            <Typography component={'span'} className={customStyles.txtBlueLight}>
                                Période de justificatif des droits :{' '}
                            </Typography>
                            <Typography component={'span'} className={customStyles.txtBlueDarkBold}>
                                {startDate}{' '}
                            </Typography>
                            au{' '}
                            <Typography component={'span'} className={customStyles.txtBlueDarkBold}>
                                {endDate}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={3}></Grid>
                    <Grid item xs={12} md={3}></Grid>
                    <Grid item xs={12} md={6}>
                        <Box className={'mt-4'}>
                            <TableSalaryBase salaries={salaries} />
                        </Box>
                    </Grid>
                    <Grid item xs={0} md={3}></Grid>
                    <Grid item xs={12} md={3}></Grid>
                    <Grid item xs={12} md={6}>
                        <Box className="mt-4">
                            <Typography className={classNames(customStyles.txtBlueDarkBold, 'text-center')}>
                                IMPORTANT : Pour la maternité/paternité, veuillez saisir les salaries nets (salaries bruts diminués de la part salariale des cotisations obligatoires d'origine légale
                                et conventionnelle et de la CSG)
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={3}></Grid>
                </Grid>
            </SGSFormLayout>
        </FormProvider>
    );
};

export default FormSalaryBaseS3201;
